import { FormHelperText, Grid, Stack } from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { listCategoriesAll } from '../../../api/products';
import { badgeOptions } from '../../../staticData/common';
import CustomButton from '../../elements/CustomButton';
import Dropdown2 from '../../elements/Dropdown2';
import FormattedTextArea from '../../elements/FormattedTextArea';
import InputField from '../../elements/InputField';

const ProductForm = ({ onSubmit, product, token }) => {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    async function fetchCustomerData() {
      try {
        const res = await listCategoriesAll(token);
        if (res.ok) {
          const data = await res.json();
          const categoryData = Array.isArray(data?.categories)
            ? data.categories.map((category) => ({
                value: category.id || '',
                label: category.name || '',
                details: category.category_details || [],
              }))
            : [];

          setCategories(categoryData);
        } else {
          throw new Error('Failed to fetch customers');
        }
      } catch (error) {
        console.error('Error fetching customers list:', error);
      }
    }
    fetchCustomerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCategoryChange = (cat, setFieldValue) => {
    setFieldValue('categoryProductRelation', {});
  };

  return (
    <>
      <Formik
        initialValues={{
          title: product?.title || '',
          price: product?.price || '',
          description: product?.description || '',
          category: product?.category || '',
          status: product?.status || 'active',
          brand: product?.brand || '',
          badge: product?.badge === 'None' ? null : product?.badge || null,
        }}
        validationSchema={Yup.object().shape({
          title: Yup.string()
            .matches(
              /^[\s\S]{1,255}$/,
              'Product Name should not exceed 255 characters'
            )
            .required('Product Name is required'),
          price: Yup.string()
            .matches(/^\$?[\d,]+(\.\d{1,2})?$/, 'Invalid price format')
            .required('Product Price is required'),
          description: Yup.string().max(700),
          brand: Yup.string().required('Brand is required'),
        })}
        onSubmit={onSubmit}>
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
          setFieldValue,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputField
                    label="Product Name"
                    id="title"
                    type="title"
                    value={values.title}
                    name="title"
                    fullWidth
                    placeholder="Product Name"
                    error={Boolean(touched.title && errors.title)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Dropdown2
                    elementName="category"
                    selected={values.category}
                    handleChange={(e) => {
                      handleChange(e);
                      handleCategoryChange(e.target.value, setFieldValue);
                    }}
                    elementLabel="Category"
                    options={categories}
                    required
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Dropdown2
                    elementName="status"
                    selected={values.status}
                    handleChange={handleChange}
                    elementLabel="Status"
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                    ]}
                    required
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputField
                    label="Product Price (approx.)"
                    id="price"
                    type="text"
                    value={`$${values.price}`}
                    name="price"
                    fullWidth
                    required
                    placeholder="Product Price"
                    error={Boolean(touched.price && errors.price)}
                    onBlur={handleBlur}
                    onChange={(e) => {
                      const formattedPrice = e.target.value.replace('$', '');
                      handleChange({
                        target: {
                          name: 'price',
                          value: formattedPrice,
                        },
                      });
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Dropdown2
                    elementName="badge"
                    selected={values.badge}
                    handleChange={handleChange}
                    elementLabel="Badge (Optional)"
                    options={badgeOptions}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputField
                    label="Brand"
                    id="brand"
                    type="brand"
                    value={values.brand}
                    name="brand"
                    fullWidth
                    placeholder="Brand"
                    error={Boolean(touched.brand && errors.brand)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <FormattedTextArea
                    label="Description"
                    id="description"
                    name="description"
                    fullWidth
                    rows={5}
                    value={values.description}
                    placeholder="Enter description"
                    error={Boolean(touched.description && errors.description)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Stack>
              </Grid>
              {errors.submit && (
                <Grid item xs={12}>
                  <FormHelperText error>{errors.submit}</FormHelperText>
                </Grid>
              )}
              <Grid item xs={12}>
                <CustomButton />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default ProductForm;
